import React, { useState } from 'react';
import './Login.css';
import { LoginCredentials, User } from '../../types/auth';
import authService from '../../services/authService';
import { phoneMask, removePhoneMask } from '../../utils/masks';

const Login: React.FC<{ onLogin: (user: User) => void }> = ({ onLogin }) => {
  const [credentials, setCredentials] = useState<LoginCredentials>({
    email: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [showSignup, setShowSignup] = useState(false);
  const [error, setError] = useState('');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setCredentials(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      const result = await authService.login(credentials.email, credentials.password);
      onLogin(result.usuario);
    } catch (error: any) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    try {
      setError('');
      
      const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
      const redirectUri = encodeURIComponent(window.location.origin);
      const scope = encodeURIComponent('openid email profile');
      const responseType = 'id_token';
      const nonce = Math.random().toString(36).substring(2, 15);
      const state = Math.random().toString(36).substring(2, 15);
      
      // Salvar state para verificação
      sessionStorage.setItem('google_auth_state', state);
      
      // URL do Google OAuth
      const googleAuthUrl = `https://accounts.google.com/o/oauth2/v2/auth?` +
        `client_id=${clientId}&` +
        `redirect_uri=${redirectUri}&` +
        `scope=${scope}&` +
        `response_type=${responseType}&` +
        `nonce=${nonce}&` +
        `state=${state}&` +
        `prompt=select_account`;
      
      // Redirecionar diretamente para Google
      window.location.href = googleAuthUrl;
      
    } catch (error) {
      setError('Erro ao inicializar autenticação Google');
    }
  };

  if (showSignup) {
    return <SignupForm onBackToLogin={() => setShowSignup(false)} onSignup={onLogin} />;
  }

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <img src="/inicial.png" alt="Logo" className="login-logo" />
          <div className="divider"></div>
          <h1 className="login-title">Bem-vindo</h1>
          <p className="login-subtitle">Entre na sua conta para continuar</p>
        </div>

        {error && (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        )}

        <form className="login-form" onSubmit={handleLogin}>
          <div className="form-group">
            <label htmlFor="email" className="form-label">
              E-mail
            </label>
            <input
              type="email"
              id="email"
              name="email"
              className="form-input"
              value={credentials.email}
              onChange={handleInputChange}
              placeholder="seu@email.com"
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="password" className="form-label">
              Senha
            </label>
            <input
              type="password"
              id="password"
              name="password"
              className="form-input"
              value={credentials.password}
              onChange={handleInputChange}
              placeholder="••••••••"
              disabled={loading}
            />
            <div className="forgot-password">
              <a href="#forgot">Esqueceu a senha?</a>
            </div>
          </div>

          <button
            type="submit"
            className="login-button"
            disabled={loading}
          >
            {loading ? 'Entrando...' : 'Entrar'}
          </button>
        </form>

        <div className="divider">ou</div>

        <button
          type="button"
          className="google-button"
          onClick={handleGoogleLogin}
          disabled={loading}
        >
          <svg width="20" height="20" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          Continuar com Google
        </button>

        <div className="signup-link">
          Não tem uma conta?{' '}
          <a href="#signup" onClick={(e) => { e.preventDefault(); setShowSignup(true); }}>
            Criar conta
          </a>
        </div>
      </div>
    </div>
  );
};

// Componente de cadastro
const SignupForm: React.FC<{ onBackToLogin: () => void; onSignup: (user: User) => void }> = ({ onBackToLogin, onSignup }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    telefone: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    
    if (name === 'telefone') {
      const masked = phoneMask(value);
      setFormData(prev => ({
        ...prev,
        [name]: masked
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      setError('As senhas não coincidem');
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      const cleanPhone = removePhoneMask(formData.telefone);
      if (cleanPhone.length < 10) {
        setError('Telefone deve ter pelo menos 10 dígitos');
        return;
      }
      
      const result = await authService.register(
        formData.name,
        formData.email,
        formData.password,
        'paciente',
        cleanPhone
      );
      onSignup(result.usuario);
    } catch (error: any) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <img src="/inicial.png" alt="Logo" className="login-logo" />
          <div className="divider"></div>
          <h1 className="login-title">Criar Conta</h1>
          <p className="login-subtitle">Preencha os dados para se cadastrar</p>
        </div>

        {error && (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        )}

        <form className="login-form" onSubmit={handleSignup}>
          <div className="form-group">
            <label htmlFor="name" className="form-label">
              Nome completo
            </label>
            <input
              type="text"
              id="name"
              name="name"
              className="form-input"
              value={formData.name}
              onChange={handleInputChange}
              placeholder="Seu nome"
              required
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="email" className="form-label">
              E-mail
            </label>
            <input
              type="email"
              id="email"
              name="email"
              className="form-input"
              value={formData.email}
              onChange={handleInputChange}
              placeholder="seu@email.com"
              required
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="telefone" className="form-label">
              Telefone
            </label>
            <input
              type="tel"
              id="telefone"
              name="telefone"
              className="form-input"
              value={formData.telefone}
              onChange={handleInputChange}
              placeholder="(11) 99999-9999"
              maxLength={15}
              required
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="password" className="form-label">
              Senha
            </label>
            <input
              type="password"
              id="password"
              name="password"
              className="form-input"
              value={formData.password}
              onChange={handleInputChange}
              placeholder="••••••••"
              required
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword" className="form-label">
              Confirmar senha
            </label>
            <input
              type="password"
              id="confirmPassword"
              name="confirmPassword"
              className="form-input"
              value={formData.confirmPassword}
              onChange={handleInputChange}
              placeholder="••••••••"
              required
              disabled={loading}
            />
          </div>

          <div className="button-group">
            <button
              type="button"
              className="cancel-button"
              onClick={onBackToLogin}
              disabled={loading}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="login-button"
              disabled={loading}
            >
              {loading ? 'Criando conta...' : 'Criar conta'}
            </button>
          </div>
        </form>

        <div className="signup-link">
          Já tem uma conta?{' '}
          <a href="#login" onClick={(e) => { e.preventDefault(); onBackToLogin(); }}>
            Fazer login
          </a>
        </div>
      </div>
    </div>
  );
};

export default Login;